import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Button, Typography } from "@material-ui/core";
import LinkIcon from "@material-ui/icons/Link";
import CheckIcon from "@material-ui/icons/Check";
import { useTranslation } from "react-i18next";
import { colors } from "~/utils/colors";
import { mixpanelTrackEvent } from "~/utils/mixpanel";

const useStyles = makeStyles(() => {
	const { background, brand } = colors;
	return {
		"@keyframes slideUp": {
			from: { transform: "translateY(100%)", opacity: 0 },
			to:   { transform: "translateY(0)",    opacity: 1 },
		},
		overlay: {
			position: "fixed",
			top: 0, left: 0, right: 0, bottom: 0,
			backgroundColor: "rgba(0, 0, 0, 0.5)",
			zIndex: 1299,
		},
		container: {
			position: "fixed",
			bottom: 0, left: 0, right: 0,
			width: "100%",
			maxWidth: 480,
			margin: "0 auto",
			display: "flex",
			flexDirection: "column",
			padding: "28px 24px 40px",
			borderTopLeftRadius: 30,
			borderTopRightRadius: 30,
			boxShadow: "0px -4px 12px rgba(0, 0, 0, 0.2)",
			backgroundColor: background?.default || "#FFFFFF",
			animation: "$slideUp 0.3s ease-out",
			zIndex: 1300,
		},
		iconWrapper: {
			display: "flex",
			justifyContent: "center",
			marginBottom: 16,
		},
		iconCircle: {
			width: 56,
			height: 56,
			borderRadius: "50%",
			backgroundColor: "#F0F4FF",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
		},
		title: {
			fontWeight: 700,
			fontSize: "clamp(18px, 3vw, 22px)",
			textAlign: "center",
			marginBottom: 8,
			color: "#333333",
		},
		subtitle: {
			fontSize: "clamp(14px, 2vw, 15px)",
			textAlign: "center",
			color: "#666666",
			lineHeight: 1.5,
			marginBottom: 24,
		},
		button: {
			backgroundColor: brand?.default || "#672E37",
			color: "#fff",
			width: "100%",
			padding: "13px 0",
			borderRadius: 100,
			textTransform: "none",
			fontWeight: "bold",
			fontSize: 15,
			"&:hover": { backgroundColor: brand?.default || "#672E37" },
		},
		buttonCopied: {
			backgroundColor: "#25A244",
			"&:hover": { backgroundColor: "#25A244" },
		},
	};
});

export default function ModalCopyLinkAuth({ open }) {
	const classes = useStyles();
	const { t } = useTranslation();
	const [copied, setCopied] = useState(false);

	useEffect(() => {
		if (open) {
			mixpanelTrackEvent("Abriu_Modal_Copiar_Link_Lembretes");
		}
	}, [open]);

	if (!open) return null;

	const getLinkToCopy = () => {
		const url = new URL(window.location.href);
		url.searchParams.set("ext", "1");
		return url.toString();
	};

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(getLinkToCopy());
		} catch {
			// fallback para ambientes sem suporte a clipboard API
			const ta = document.createElement("textarea");
			ta.value = getLinkToCopy();
			ta.style.position = "fixed";
			ta.style.opacity = "0";
			document.body.appendChild(ta);
			ta.focus();
			ta.select();
			document.execCommand("copy");
			document.body.removeChild(ta);
		}
		setCopied(true);
		setTimeout(() => setCopied(false), 3000);
	};

	return (
		<>
			<div className={classes.overlay} />

			<div className={classes.container}>
				<div className={classes.iconWrapper}>
					<div className={classes.iconCircle}>
						<LinkIcon style={{ fontSize: 28, color: "#4A6CF7" }} />
					</div>
				</div>

				<Typography className={classes.title}>
					{t("Use an external browser")}
				</Typography>

				<Typography className={classes.subtitle}>
					{t("To authenticate via WhatsApp copy this link and open it in Chrome Safari or another external browser")}
				</Typography>

				<Button
					className={`${classes.button} ${copied ? classes.buttonCopied : ""}`}
					onClick={handleCopy}
					startIcon={copied ? <CheckIcon /> : <LinkIcon />}
				>
					{copied ? t("Link copied") : t("Copy link")}
				</Button>
			</div>
		</>
	);
}
